import React,{useState,useEffect} from 'react';
import clinicService from '../../../../../services/clinic.service'
import Loading from '../../../../common/Loading'
import { useParams } from 'react-router-dom'

const AccountStatus = () => {
    const { clinicId } = useParams();
    const [loading, setLoading] = useState(true);
    const [serviceStatus, setServiceStatus] = useState([]);
    const [completionStats, setCompletionStats] = useState({
        completed: 0,
        pending: 0,
        incomplete: 0,
        total: 0
    });
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const fetchAccountStatus = async () => {
            try {
                setLoading(true);
                const response = await clinicService.getClinicAccountStatus(clinicId);
                setServiceStatus(response.data.services);
                calculateStats(response.data.services);
            } catch (error) {
                console.error('Error fetching account status:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAccountStatus();
    }, [clinicId]);

    const calculateStats = (services) => {
        const completed = services.filter(s => s.status === 'COMPLETED').length;
        const pending = services.filter(s => s.status === 'PENDING').length;
        const incomplete = services.filter(s => s.status === 'INCOMPLETE').length;

        setCompletionStats({
            completed,
            pending,
            incomplete,
            total: services.length
        });
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(clinicId);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const getServiceIcon = (serviceName) => {
        switch(serviceName) {
            case "Clinic Agreements":
                return <i className="bi bi-file-earmark-medical me-2"></i>;
            case "Clinic Verification":
                return <i className="bi bi-shield-check me-2"></i>;
            case "Clinic Storage":
                return <i className="bi bi-database me-2"></i>;
            case "Clinic Reminder Setting":
                return <i className="bi bi-bell me-2"></i>;
            case "Clinic Hours":
                return <i className="bi bi-clock me-2"></i>;
            case "Clinic Recall Setting":
                return <i className="bi bi-calendar-check me-2"></i>;
            case "Clinic Recall Times":
                return <i className="bi bi-watch me-2"></i>;
            case "Clinic Users":
                return <i className="bi bi-people me-2"></i>;
            case "SMS Templates":
                return <i className="bi bi-chat me-2"></i>;
            case "Email Templates":
                return <i className="bi bi-envelope me-2"></i>;
            case "Call Templates":
                return <i className="bi bi-telephone me-2"></i>;
            case "Patients":
                return <i className="bi bi-person-badge me-2"></i>;
            default:
                return <i className="bi bi-question-circle me-2"></i>;
        }
    };

    const getStatusBadge = (status) => {
        switch(status) {
            case "COMPLETED":
                return <span className="badge service-status ms-2 text-success status-completed"><i className="bi bi-check-circle-fill"></i> COMPLETED</span>;
            case "PENDING":
                return <span className="badge service-status ms-2 text-primary status-pending"><i className="bi bi-hourglass-split"></i> PENDING</span>;
            case "INCOMPLETE":
                return <span className="badge service-status text-danger ms-2 status-incomplete"><i className="bi bi-exclamation-circle-fill"></i> INCOMPLETE</span>;
            default:
                return <span className="badge service-status text-danger status-pending ms-2"><i className="bi bi-question-circle-fill"></i></span>;
        }
    };

    const completionPercentage = completionStats.total > 0
        ? Math.round((completionStats.completed / completionStats.total) * 100)
        : 0;

    return (
        <div className="card card-padding shadow-none">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0" style={{ color: "#012970" }}>
                        <i className="bi bi-clipboard2-pulse"></i> Account Status
                    </h5>
                    <div className="d-flex align-items-center">
                        <span className="badge bg-light text-dark me-2">
                            Clinic ID: {clinicId}
                        </span>
                        <button
                            onClick={copyToClipboard}
                            className="btn btn-sm btn-outline-primary"
                            title="Copy to clipboard"
                        >
                            {copied ? (
                                <i className="bi bi-check2"></i>
                            ) : (
                                <i className="bi bi-copy"></i>
                            )}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <Loading />
                ) : (
                    <>
                        {/* Progress Bar */}
                        <div className="mb-4">
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Completion Progress</span>
                                <span className="fw-semibold">
                                    {completionPercentage}% ({completionStats.completed}/{completionStats.total} services)
                                </span>
                            </div>
                            <div className="progress" style={{ height: "8px" }}>
                                <div
                                    className="progress-bar bg-success"
                                    role="progressbar"
                                    style={{ width: `${completionPercentage}%` }}
                                ></div>
                            </div>
                        </div>

                        {/* Status Summary */}
                        <div className="d-flex flex-wrap gap-3 mb-4">
                            <div className="d-flex align-items-center">
                                <span className="badge bg-success rounded-circle p-2 me-2"></span>
                                <span className="text-success fw-normal">
                                    {completionStats.completed} COMPLETED
                                </span>
                            </div>
                            <div className="d-flex align-items-center">
                                <span className="badge bg-primary rounded-circle p-2 me-2"></span>
                                <span className="text-primary fw-normal">
                                    {completionStats.pending} PENDING
                                </span>
                            </div>
                            <div className="d-flex align-items-center">
                                <span className="badge bg-danger rounded-circle p-2 me-2"></span>
                                <span className="text-danger fw-normal">
                                    {completionStats.incomplete} INCOMPLETE
                                </span>
                            </div>
                        </div>

                        {/* Services List */}
                        <div className="list-group">
                            {serviceStatus.map((item, index) => (
                                <div
                                    key={index}
                                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                >
                                    <div className="d-flex align-items-center">
                                        {getServiceIcon(item.serviceName)}
                                        <span className="ms-2">{item.serviceName}</span>
                                    </div>
                                    <div>
                                        {getStatusBadge(item.status)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AccountStatus;